import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { loadModels, getFaceDescriptor, findBestMatch } from '../services/faceService';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MapPin, Camera, CheckCircle, Clock, AlertTriangle, Loader2, QrCode, X } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const METHODS = [
  { id: 'gps', label: 'GPS', icon: MapPin },
  { id: 'face', label: 'Visage', icon: Camera },
];

export default function ClockPage() {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [todayAttendance, setTodayAttendance] = useState(null);
  const [method, setMethod] = useState('gps');
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [faceStatus, setFaceStatus] = useState('idle'); // idle, loading, detected, verified, failed
  const [faceDescriptors, setFaceDescriptors] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadTodayAttendance();
  }, []);

  const loadTodayAttendance = async () => {
    try {
      const { data } = await api.get('/attendance/today');
      setTodayAttendance(data);
    } catch {}
  };

  const getLocation = useCallback(() => {
    setGettingLocation(true);
    setLocationError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGettingLocation(false);
      },
      (err) => {
        setLocationError('Impossible d\'obtenir votre position GPS');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }, []);

  useEffect(() => {
    if (method === 'gps') getLocation();
    if (method === 'face') {
      startCamera();
      loadFaceDescriptors();
    } else {
      stopCamera();
    }
  }, [method]);

  const startCamera = async () => {
    try {
      setFaceStatus('loading');
      await loadModels();
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 640, height: 480 } });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraActive(true);
      setFaceStatus('idle');
    } catch (err) {
      toast.error('Impossible d\'accéder à la caméra');
      setFaceStatus('failed');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
    setFaceStatus('idle');
  };

  const loadFaceDescriptors = async () => {
    try {
      const { data } = await api.get('/face/descriptors');
      setFaceDescriptors(data);
    } catch {}
  };

  const verifyFace = async () => {
    if (!videoRef.current) return null;
    setFaceStatus('loading');
    try {
      const result = await getFaceDescriptor(videoRef.current);
      if (!result) {
        setFaceStatus('failed');
        toast.error('Aucun visage détecté. Regardez la caméra.');
        return null;
      }
      // Comparer avec les descripteurs enregistrés
      if (!user.hasFaceRegistered) {
        setFaceStatus('detected');
        return { descriptor: result.descriptor, verified: false, confidence: 0 };
      }
      const match = findBestMatch(result.descriptor, faceDescriptors);
      if (match && match.match.userId === user.id) {
        setFaceStatus('verified');
        return { descriptor: result.descriptor, verified: true, confidence: match.confidence };
      } else {
        setFaceStatus('failed');
        toast.error('Visage non reconnu');
        return null;
      }
    } catch (err) {
      setFaceStatus('failed');
      return null;
    }
  };

  const handleClock = async (type) => {
    setLoading(true);
    try {
      let faceData = {};
      if (method === 'face') {
        const face = await verifyFace();
        if (!face && user.hasFaceRegistered) { setLoading(false); return; }
        faceData = { faceVerified: face?.verified, faceConfidence: face?.confidence };
      }

      const payload = {
        lat: location?.lat,
        lng: location?.lng,
        method,
        ...faceData
      };

      const endpoint = type === 'in' ? '/attendance/clock-in' : '/attendance/clock-out';
      const { data } = await api.post(endpoint, payload);
      toast.success(data.message || (type === 'in' ? 'Entrée enregistrée !' : 'Sortie enregistrée !'));
      if (!data.isWithinGeofence && data.distance) {
        toast(`📍 Hors zone : ${data.distance}m du bureau`, { icon: '⚠️' });
      }
      await loadTodayAttendance();
      stopCamera();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors du pointage');
    } finally {
      setLoading(false);
    }
  };

  const isClockedIn = todayAttendance?.clockIn && !todayAttendance?.clockOut;
  const isClockedOut = todayAttendance?.clockIn && todayAttendance?.clockOut;
  const canClockIn = !todayAttendance?.clockIn;
  const canClockOut = isClockedIn;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Pointage</h1>
        <p className="text-gray-500 mt-1">{format(currentTime, 'EEEE dd MMMM yyyy', { locale: fr })}</p>
      </div>

      {/* Horloge */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 mb-6 text-center text-white shadow-lg">
        <p className="text-6xl font-bold font-mono tracking-wider">
          {format(currentTime, 'HH:mm:ss')}
        </p>
        <p className="text-blue-200 mt-2 text-sm">
          {isClockedIn ? `Au bureau depuis ${format(new Date(todayAttendance.clockIn), 'HH:mm')}` :
           isClockedOut ? `Journée terminée · ${((todayAttendance.totalMinutes || 0) / 60).toFixed(1)}h` :
           'Pas encore pointé'}
        </p>
      </div>

      {/* Statut du jour */}
      {todayAttendance && (
        <div className="bg-white rounded-2xl p-5 mb-5 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-700 mb-3">Aujourd'hui</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-green-50 rounded-xl">
              <p className="text-xs text-gray-500 mb-1">Entrée</p>
              <p className="text-xl font-bold text-green-600">
                {todayAttendance.clockIn ? format(new Date(todayAttendance.clockIn), 'HH:mm') : '--:--'}
              </p>
              {todayAttendance.isLate && (
                <p className="text-xs text-orange-500 mt-1">+{todayAttendance.lateMinutes} min retard</p>
              )}
            </div>
            <div className="text-center p-3 bg-red-50 rounded-xl">
              <p className="text-xs text-gray-500 mb-1">Sortie</p>
              <p className="text-xl font-bold text-red-500">
                {todayAttendance.clockOut ? format(new Date(todayAttendance.clockOut), 'HH:mm') : '--:--'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Méthode de pointage */}
      {!isClockedOut && (
        <>
          <div className="bg-white rounded-2xl p-5 mb-5 shadow-sm border border-gray-100">
            <h3 className="font-semibold text-gray-700 mb-3">Méthode de pointage</h3>
            <div className="flex gap-3">
              {METHODS.map(m => (
                <button key={m.id} onClick={() => setMethod(m.id)}
                  className={clsx(
                    'flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium flex-1 justify-center transition',
                    method === m.id ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  )}>
                  <m.icon className="w-4 h-4" /> {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* GPS Status */}
          {method === 'gps' && (
            <div className="bg-white rounded-2xl p-5 mb-5 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3">
                <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center',
                  location ? 'bg-green-100' : locationError ? 'bg-red-100' : 'bg-blue-100')}>
                  {gettingLocation ? <Loader2 className="w-5 h-5 text-blue-500 animate-spin" /> :
                   location ? <MapPin className="w-5 h-5 text-green-600" /> :
                   <AlertTriangle className="w-5 h-5 text-red-500" />}
                </div>
                <div>
                  <p className="font-medium text-gray-700 text-sm">
                    {gettingLocation ? 'Localisation en cours...' :
                     location ? 'Position GPS obtenue' :
                     locationError || 'Position non disponible'}
                  </p>
                  {location && (
                    <p className="text-xs text-gray-400">Précision : ±{Math.round(location.accuracy)}m</p>
                  )}
                </div>
                {!location && !gettingLocation && (
                  <button onClick={getLocation} className="ml-auto text-blue-600 text-sm font-medium">
                    Réessayer
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Camera Face */}
          {method === 'face' && (
            <div className="bg-white rounded-2xl p-5 mb-5 shadow-sm border border-gray-100">
              <div className="relative aspect-video bg-black rounded-xl overflow-hidden mb-3">
                <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
                {!cameraActive && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  </div>
                )}
                {faceStatus === 'verified' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-green-500/30 backdrop-blur-sm">
                    <CheckCircle className="w-16 h-16 text-white" />
                  </div>
                )}
              </div>
              <p className="text-sm text-center text-gray-500">
                {faceStatus === 'loading' ? 'Analyse en cours...' :
                 faceStatus === 'verified' ? '✅ Visage vérifié' :
                 faceStatus === 'failed' ? '❌ Échec de reconnaissance' :
                 'Placez votre visage face à la caméra'}
              </p>
            </div>
          )}

          {/* Boutons pointage */}
          <div className="grid grid-cols-2 gap-4">
            {canClockIn && (
              <button onClick={() => handleClock('in')} disabled={loading || (method === 'gps' && !location)}
                className="bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white font-bold py-5 rounded-2xl transition flex flex-col items-center gap-1 shadow-md">
                {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Clock className="w-6 h-6" />}
                <span>Pointer l'entrée</span>
              </button>
            )}
            {canClockOut && (
              <button onClick={() => handleClock('out')} disabled={loading || (method === 'gps' && !location)}
                className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-bold py-5 rounded-2xl transition flex flex-col items-center gap-1 shadow-md col-start-2">
                {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Clock className="w-6 h-6" />}
                <span>Pointer la sortie</span>
              </button>
            )}
          </div>
        </>
      )}

      {isClockedOut && (
        <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-gray-100">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-800 mb-2">Journée enregistrée</h3>
          <p className="text-gray-500">
            {((todayAttendance.totalMinutes || 0) / 60).toFixed(1)}h travaillées aujourd'hui
          </p>
          {(todayAttendance.overtimeMinutes || 0) > 0 && (
            <p className="text-blue-600 text-sm mt-1">
              +{((todayAttendance.overtimeMinutes || 0) / 60).toFixed(1)}h supplémentaires
            </p>
          )}
        </div>
      )}
    </div>
  );
}
